/**
 * ObstructionControls - Obstruction drawing and editing controls
 * Displayed in Site mode under Roof section
 */

import React from 'react';
import { Square, Trash2, Circle, Pentagon } from 'lucide-react';
import { AuroraCard, AuroraCardSection, AuroraButton } from './AuroraCard';

export type ObstructionDrawMode = 'rectangle' | 'circle' | 'polygon';

interface ObstructionControlsProps {
  // Polygon context
  selectedPolygon: number | null;

  // Drawing state
  isDrawing: boolean;
  drawMode?: ObstructionDrawMode;
  onStartDrawing: (mode: ObstructionDrawMode) => void;
  onStopDrawing: () => void;

  // Obstruction selection
  obstructions: any[];
  selectedObstruction: { polygonId: number; index: number } | null;
  onSelectObstruction: (selection: { polygonId: number; index: number } | null) => void;
  onDeleteObstruction: () => void;

  // Editing state
  editingObstruction: { polygonId: number; index: number } | null;
  onStartEdit?: () => void;
  onStopEdit?: () => void;

  // Collapse state
  collapsed?: boolean;
  onToggle?: () => void;
}

export const ObstructionControls: React.FC<ObstructionControlsProps> = ({
  selectedPolygon,
  isDrawing,
  drawMode = 'rectangle',
  onStartDrawing,
  onStopDrawing,
  obstructions,
  selectedObstruction,
  onSelectObstruction,
  onDeleteObstruction,
  editingObstruction,
  onStartEdit,
  onStopEdit,
  collapsed = false,
  onToggle,
}) => {
  return (
    <AuroraCard
      title="Obstructions"
      collapsed={collapsed}
      onToggle={onToggle}
    >
      <AuroraCardSection>
        {selectedPolygon === null ? (
          <div className="text-sm text-gray-600 italic bg-gray-50 rounded-lg p-3 border-2 border-gray-200">
            Select a polygon first to add obstructions
          </div>
        ) : (
          <div className="space-y-3">
            {/* Drawing Controls */}
            {!isDrawing ? (
              <>
                {/* Drawing Mode Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Draw Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => onStartDrawing('rectangle')}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Square className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Rectangle</span>
                    </button>
                    <button
                      onClick={() => onStartDrawing('circle')}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Circle className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Circle</span>
                    </button>
                    <button
                      onClick={() => onStartDrawing('polygon')}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border-2 border-gray-300 hover:border-[#EBFD03] hover:bg-[#EBFD03]/10 transition-colors"
                    >
                      <Pentagon className="h-5 w-5 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-700">Polygon</span>
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-600">
                  Choose a shape and draw on the map (chimneys, vents, skylights, etc.)
                </p>
              </>
            ) : (
              <>
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 text-red-700 text-sm font-semibold mb-1">
                    {drawMode === 'rectangle' && <Square className="h-4 w-4" />}
                    {drawMode === 'circle' && <Circle className="h-4 w-4" />}
                    {drawMode === 'polygon' && <Pentagon className="h-4 w-4" />}
                    <span>Drawing {drawMode.charAt(0).toUpperCase() + drawMode.slice(1)}</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    {drawMode === 'rectangle' && 'Click and drag to draw a rectangle'}
                    {drawMode === 'circle' && 'Click center, then drag to set radius'}
                    {drawMode === 'polygon' && 'Click to place vertices, double-click to finish'}
                  </p>
                </div>

                <AuroraButton
                  variant="secondary"
                  size="md"
                  onClick={onStopDrawing}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <span>Done Adding</span>
                </AuroraButton>
              </>
            )}

            {/* Obstruction List */}
            {obstructions.length > 0 && !isDrawing && (
              <div className="mt-4 pt-4 border-t-2 border-gray-200">
                <h4 className="text-xs font-semibold text-gray-600 mb-2">
                  Obstructions ({obstructions.length})
                </h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {obstructions.map((_, index) => {
                    const isSelected = selectedObstruction?.index === index;
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-[#EBFD03]/20 border-2 border-[#EBFD03]'
                            : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          if (selectedPolygon !== null) {
                            onSelectObstruction({ polygonId: selectedPolygon, index });
                          }
                        }}
                      >
                        <span className="text-sm text-gray-700">Obstruction {index + 1}</span>
                        {isSelected && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteObstruction();
                            }}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Delete obstruction"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Editing Instructions */}
            {selectedObstruction && !isDrawing && !editingObstruction && (
              <div className="mt-2 bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-700 space-y-1">
                  <span className="font-semibold block">Edit selected:</span>
                  • Drag corners to reshape<br />
                  • Drag edges to resize<br />
                  • Drag center to move<br />
                  • Double-click for vertex edit
                </p>
              </div>
            )}

            {/* Editing Obstruction Mode */}
            {editingObstruction && !isDrawing && onStopEdit && (
              <div className="mt-2 bg-green-50 border-2 border-green-500 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-700 text-sm font-semibold mb-2">
                  <Pentagon className="h-4 w-4" />
                  <span>Editing Obstruction {editingObstruction.index + 1}</span>
                </div>
                <div className="text-xs text-gray-700 space-y-1 mb-3">
                  <p>• Drag vertices to adjust shape</p>
                  <p>• Option+click edge to add vertex</p>
                  <p>• Option+click vertex to remove it</p>
                  <p className="text-gray-600 italic mt-2">Press ESC to exit edit mode</p>
                </div>
                <AuroraButton
                  variant="primary"
                  size="md"
                  onClick={onStopEdit}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <span>Done Editing</span>
                </AuroraButton>
              </div>
            )}
          </div>
        )}
      </AuroraCardSection>
    </AuroraCard>
  );
};
